import {
  AmenityStatus as PackagesApiAmenityStatus,
  CancellationPolicyV2 as PackagesApiCancellationPolicyV2,
  LodgingMediaAsset as PackagesApiLodgingMediaAsset,
  PackagedAirV2,
  PackagedRoomComponentV2,
  PackageDisplayData,
  Prices as PackagesApiPrices,
} from "@capone/packages-api";
import {
  AmenityEnum,
  AmenityStatus,
  BedTypeEnum,
  CancellationReason,
  CancellationSummary,
  CategoryEnum,
  EffectiveRangeEnum,
  HotelCancellationPolicyV2,
  HotelCancellationPolicyV2Enum,
  LodgingMediaAsset,
  LodgingMediaAssetEnum,
  PackagePricing,
  Prices,
  RoomInfo,
  RoomInfoProducts,
} from "redmond";

/**
 * `@capone/packages-api` and redmond generate separate TS representations
 * (string-literal unions vs. real `enum`s) for enums that mirror the same
 * backend wire values. TypeScript treats these as structurally distinct
 * types even though the string values are identical by construction, so a
 * direct assignment is rejected. This narrows a v2 string value to a
 * redmond enum member, validating membership rather than blindly casting.
 */
const toLocalEnumValue = <T extends Record<string, string>>(
  localEnum: T,
  value: string
): T[keyof T] | undefined =>
  (Object.values(localEnum) as string[]).includes(value)
    ? (value as T[keyof T])
    : undefined;

/**
 * Converts a `@capone/packages-api` `Prices` (fiat + accountSpecific) into
 * redmond's `Prices` (fiat + rewards + accountSpecific). v2 has no equivalent of
 * the legacy `rewards` map (CTP's own `rewards` fallback is gated behind the
 * separately-sourced, flight-side `packagesShopPricing` and is out of scope
 * here), so it's populated empty rather than fabricated.
 */
const toPrices = (prices: PackagesApiPrices): Prices => ({
  fiat: prices.fiat,
  rewards: {},
  accountSpecific: prices.accountSpecific,
});

const toAmenityStatus = (
  amenity: PackagesApiAmenityStatus
): AmenityStatus | undefined => {
  const localAmenity = toLocalEnumValue(AmenityEnum, amenity.amenity);
  if (localAmenity == null) return undefined;

  return {
    amenity: localAmenity,
    displayName: amenity.displayName,
    details: amenity.details,
  };
};

const toLodgingMediaAsset = (
  asset: PackagesApiLodgingMediaAsset
): LodgingMediaAsset | undefined => {
  if (asset.LodgingMediaAsset === "Video") {
    return {
      url: asset.url,
      thumbnail: asset.thumbnail,
      LodgingMediaAsset: LodgingMediaAssetEnum.Video,
    };
  }

  const category =
    asset.category != null
      ? toLocalEnumValue(CategoryEnum, asset.category)
      : undefined;

  if (asset.LodgingMediaAsset === "ResizableImage") {
    return {
      url: asset.url,
      category,
      LodgingMediaAsset: LodgingMediaAssetEnum.ResizableImage,
    };
  }

  return {
    url: asset.url,
    category,
    LodgingMediaAsset: LodgingMediaAssetEnum.NonResizableImage,
  };
};

/**
 * Builds the `RoomInfo` Book/Review need from the selected room's
 * `shopDetails`. Returns `undefined` when `shopDetails` isn't populated on the
 * snapshot (see the plan's note: confirm during implementation under what
 * conditions the BE populates this).
 */
export const toRoomInfo = (
  component: PackagedRoomComponentV2
): RoomInfo | undefined => {
  const { shopDetails } = component;
  if (shopDetails == null) {
    return undefined;
  }

  const firstRoomBedTypes = shopDetails.bedTypes.rooms[0]?.bedTypes;

  return {
    roomId: shopDetails.roomId?.value ?? "",
    name: shopDetails.name,
    description: shopDetails.description,
    maxAdults: shopDetails.maxAdults,
    maxChildren: shopDetails.maxChildren,
    beds: {
      description: firstRoomBedTypes?.description ?? "",
      bedTypes: (firstRoomBedTypes?.bedTypes ?? [])
        .map((bedType) => {
          const localBedType = toLocalEnumValue(
            BedTypeEnum,
            bedType.bedType
          );
          return localBedType != null
            ? { bedType: localBedType, description: bedType.description }
            : undefined;
        })
        .filter((bedType): bedType is NonNullable<typeof bedType> =>
          bedType != null
        ),
    },
    amenities: shopDetails.amenities
      .map(toAmenityStatus)
      .filter((amenity): amenity is AmenityStatus => amenity != null),
    sizeSqFt: shopDetails.sizeSqFt,
    media: shopDetails.media
      .map(toLodgingMediaAsset)
      .filter((asset): asset is LodgingMediaAsset => asset != null),
  };
};

/**
 * Builds the `RoomInfoProducts` shape Book/Review read `selectedRoom` as.
 * `products` is intentionally empty under v2: the only genuine consumers of
 * `RoomProduct` fields (`.bedTypes.occupancy`, `.loyaltyProgramCode`,
 * `getSelectedOpaqueRoomRate`) are either already optional-chained cosmetic
 * fallbacks or dead code (confirmed no callers) — fabricating a full
 * `RoomProduct` (pricing, `opaqueQuoteRequest`, etc. with no v2 source) would
 * repeat the mistake the file's original TODOs warned against.
 */
export const toRoomInfoProducts = (
  component: PackagedRoomComponentV2
): RoomInfoProducts | undefined => {
  const roomInfo = toRoomInfo(component);
  if (roomInfo == null) return undefined;

  return { roomInfo, products: [] };
};

export const toSelectedRoomRateId = (
  component: PackagedRoomComponentV2
): string | undefined => component.shopDetails?.rateId?.value;

/**
 * The lodging cancellation summary, independent of `roomInfoProducts` —
 * replaces `lodgingShopResponse.cancellationSummary` under v2.
 */
export const toCancellationSummary = (
  component: PackagedRoomComponentV2
): CancellationSummary | undefined => {
  const { cancellationSummary } = component.shopDetails ?? {};
  if (cancellationSummary == null) return undefined;

  return {
    reasons: cancellationSummary.reasons
      .map((reason) => toLocalEnumValue(CancellationReason, reason))
      .filter((reason): reason is CancellationReason => reason != null),
  };
};

/**
 * Assembles the `PackagePricing` shape Book's checkout-init and Review's
 * pricing display need, from the package snapshot's top-level pricing plus
 * the selected room/air components' own `productPricing`.
 */
export const toPackagePricingFromSnapshot = (
  packageDisplayData: PackageDisplayData,
  airComponent: PackagedAirV2 | undefined,
  roomComponent: PackagedRoomComponentV2 | undefined
): PackagePricing => {
  const { pricing } = packageDisplayData;

  return {
    pricePerTraveler: toPrices(pricing.pricePerTraveler),
    strikethroughPricePerTraveler: toPrices(
      pricing.strikethroughPricePerTraveler
    ),
    totalPrice: toPrices(pricing.totalPrice),
    totalPackageSavings: toPrices(pricing.totalPackageSavings),
    strikethroughTotalPrice: toPrices(pricing.strikethroughTotalPrice),
    totalDueTodayPrice:
      pricing.totalDueTodayPrice != null
        ? toPrices(pricing.totalDueTodayPrice)
        : undefined,
    dueAtHotelPrice:
      pricing.dueAtHotelPrice != null
        ? toPrices(pricing.dueAtHotelPrice)
        : undefined,
    lodgingTotalPrice: toPrices(pricing.lodgingTotalPrice),
    lodgingSubTotalPrice:
      pricing.lodgingSubTotalPrice != null
        ? toPrices(pricing.lodgingSubTotalPrice)
        : undefined,
    lodgingTotalDueTodayPrice:
      roomComponent != null
        ? toPrices(roomComponent.productPricing.totalDueTodayPrice)
        : undefined,
    flightTotalDueTodayPrice:
      airComponent != null
        ? toPrices(airComponent.productPricing.totalDueTodayPrice)
        : undefined,
  };
};

/**
 * The minimal `PackagePricing` Review's hotel-pricing display fallback needs,
 * built from the selected room component's own `productPricing` alone (not
 * the full package snapshot) — for use when only the room is available.
 */
export function toReviewPricing(
  roomComponent: PackagedRoomComponentV2
): PackagePricing;
export function toReviewPricing(
  roomComponent: PackagedRoomComponentV2 | undefined
): PackagePricing | undefined;
export function toReviewPricing(
  roomComponent: PackagedRoomComponentV2 | undefined
): PackagePricing | undefined {
  if (roomComponent == null) return undefined;
  const { productPricing } = roomComponent;

  return {
    pricePerTraveler: toPrices(productPricing.pricePerTraveler),
    strikethroughPricePerTraveler: toPrices(
      productPricing.strikethroughPricePerTraveler
    ),
    totalPrice: toPrices(productPricing.totalPrice),
    totalPackageSavings: toPrices(productPricing.totalSavings),
    strikethroughTotalPrice: toPrices(productPricing.strikethroughTotalPrice),
    totalDueTodayPrice: toPrices(productPricing.totalDueTodayPrice),
    dueAtHotelPrice:
      productPricing.dueAtHotelPrice != null
        ? toPrices(productPricing.dueAtHotelPrice)
        : undefined,
    lodgingTotalDueTodayPrice: toPrices(productPricing.totalDueTodayPrice),
  };
}

/**
 * Converts the room's v2 refundability policy (preferred over the legacy
 * `cancellationPolicy`, per `RoomShopDetails`'s own doc comment) into
 * redmond's `HotelCancellationPolicyV2` — field-for-field identical to
 * `@capone/packages-api`'s `CancellationPolicyV2`, modulo the usual
 * string-literal-union vs. real-enum discriminant mismatch. Feeds
 * halifax's `getCancellationPolicyInfoV2`.
 */
export const toHotelCancellationPolicyV2 = (
  policy: PackagesApiCancellationPolicyV2
): HotelCancellationPolicyV2 | undefined => {
  if (policy.CancellationPolicyV2 === "Unknown") {
    return {
      CancellationPolicyV2: HotelCancellationPolicyV2Enum.Unknown,
      primaryText: policy.primaryText,
    };
  }

  const effectiveRange =
    policy.effectiveRange.EffectiveRange === "Available"
      ? {
          EffectiveRange: EffectiveRangeEnum.Available as const,
          startTime: policy.effectiveRange.startTime,
          endTime: policy.effectiveRange.endTime,
        }
      : { EffectiveRange: EffectiveRangeEnum.NotAvailable as const };

  switch (policy.CancellationPolicyV2) {
    case "FullyRefundable":
      return {
        CancellationPolicyV2: HotelCancellationPolicyV2Enum.FullyRefundable,
        primaryText: policy.primaryText,
        secondaryText: policy.secondaryText,
        cfarInfoBannerText: policy.cfarInfoBannerText,
        effectiveRange,
      };
    case "NonRefundable":
      return {
        CancellationPolicyV2: HotelCancellationPolicyV2Enum.NonRefundable,
        primaryText: policy.primaryText,
        secondaryText: policy.secondaryText,
        cfarInfoBannerText: policy.cfarInfoBannerText,
        effectiveRange,
      };
    case "PartiallyRefundable":
      return {
        CancellationPolicyV2:
          HotelCancellationPolicyV2Enum.PartiallyRefundable,
        primaryText: policy.primaryText,
        secondaryText: policy.secondaryText,
        cfarInfoBannerText: policy.cfarInfoBannerText,
        effectiveRange,
      };
    default:
      return undefined;
  }
};
